import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { isDemoId, DEMO_GOOGLE_REVIEWS, DEMO_PROSPECT_METADATA } from '../utils/demoUtils';
import { callEdge } from '../lib/apiClient';

interface GoogleReview {
  author_name: string;
  rating: number;
  text?: string;
  time: string;
  profile_photo_url?: string;
}

interface GoogleBusinessInfo {
  name: string;
  rating: number;
  reviews_count: number;
  reviews: GoogleReview[];
  place_id: string;
  address?: string;
  website?: string;
  phone?: string;
  url: string;
}

interface UseGoogleReviewsOptions {
  businessName?: string;
  location?: string;
  prospectId: string;
  domain: string;
  languageCode?: string;
  reviewsLimit?: number;
  autoStart?: boolean;
}

interface UseGoogleReviewsReturn {
  businessInfo: GoogleBusinessInfo | null;
  loading: boolean;
  error: string | null;
  taskStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'not_found';
  estimatedWaitTime: number;
  isPolling: boolean;
  startTask: () => Promise<void>;
  checkStatus: () => Promise<void>;
  retry: () => Promise<void>;
  forceRefresh: () => Promise<void>;
}

/**
 * Hook personnalisé pour gérer les tâches Google Reviews asynchrones
 * Gère automatiquement le polling et les états de la tâche
 */
  /**
 * Hook personnalisé pour gérer les tâches Google Reviews asynchrones.
 * Gère automatiquement le polling et les états de la tâche.
 *
 * @param {UseGoogleReviewsOptions} options - Les options du hook.
 * @property {string} businessName - Le nom de l'entreprise.
 * @property {string} location - La localisation de l'entreprise.
 * @property {string} prospectId - L'identifiant du prospect.
 * @property {string} domain - Le nom de domaine.
 * @property {string} languageCode - Le code de langue.
 * @property {number} reviewsLimit - La quantité d'avis à récupérer.
 * @property {boolean} autoStart - Si la tâche doit être démarrée automatiquement.
 * @returns {UseGoogleReviewsReturn} - Les informations de la tâche.
 */
export function useGoogleReviews(options: UseGoogleReviewsOptions): UseGoogleReviewsReturn {
  const { businessName: providedBusinessName, location: providedLocation = '', prospectId, domain, languageCode = 'fr', reviewsLimit = 3, autoStart = false } = options;

  // Cache to prevent duplicate API calls (refs must come before state hooks)
  const dataCache = useRef<{ [key: string]: GoogleBusinessInfo | null }>({});
  const loadingProspects = useRef<Map<string, Promise<GoogleBusinessInfo | null>>>(new Map());

  const [businessInfo, setBusinessInfo] = useState<GoogleBusinessInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<'pending' | 'processing' | 'completed' | 'failed' | 'not_found'>('not_found');
  const [estimatedWaitTime, setEstimatedWaitTime] = useState<number>(0);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [businessName, setBusinessName] = useState<string>(providedBusinessName || '');
  const [location, setLocation] = useState<string>(providedLocation);

  // Récupère les données company et companyLocation depuis la table prospects
  const fetchProspectData = useCallback(async () => {
    try {
      if (isDemoId(prospectId)) {
        return {
          company: DEMO_PROSPECT_METADATA.company,
          location: DEMO_PROSPECT_METADATA.location
        };
      }

      const { data: prospect, error: prospectError } = await supabase
        .from('prospects')
        .select('metadata')
        .eq('id', prospectId)
        .single();

      if (prospectError) {
        throw new Error(`Erreur DB: ${prospectError.message}`);
      }

      if (!prospect?.metadata) {
        return { company: '', companyLocation: '' };
      }

      const company = prospect.metadata.company || '';
      const location = prospect.metadata.location || '';

      return { company, location };
    } catch (err) {
      throw err;
    }
  }, [prospectId]);

  // Vérifie d'abord si des données existent déjà dans la base de données
  const checkExistingData = useCallback(async () => {
    try {
      if (!prospectId) {
        return null;
      }

      // Check cache first
      if (dataCache.current[prospectId]) {
        console.log('📦 [Google Reviews] Données en cache pour:', prospectId);
        return dataCache.current[prospectId];
      }

      // If request already in progress, wait for it
      const existingPromise = loadingProspects.current.get(prospectId);
      if (existingPromise) {
        console.log('⏳ [Google Reviews] Attente de la requête en cours pour:', prospectId);
        return await existingPromise;
      }

      // Create new request promise
      const requestPromise = (async () => {
        try {
          // Use Edge Function to avoid 406 errors (service role key)
          const allData = await callEdge<{ prospectId: string }, any>('get-prospect-data', {
            prospectId
          });

          console.log('📦 [Google Reviews] Réponse get-prospect-data:', { prospectId, hasGoogleReviews: !!allData?.googleReviews, googleReviewsData: allData?.googleReviews });

          const data = allData?.googleReviews;

          if (!data) {
            console.log('⚠️ [Google Reviews] Aucune donnée googleReviews dans la réponse');
            dataCache.current[prospectId] = null;
            return null;
          }

          const result = {
            name: data.name || '',
            rating: data.rating || 0,
            reviews_count: data.reviews_count || 0,
            reviews: data.reviews || [],
            place_id: data.place_id || '',
            address: data.address || '',
            website: data.website || '',
            phone: data.phone || '',
            url: data.url || ''
          };

          // Cache the result
          dataCache.current[prospectId] = result;
          return result;
        } finally {
          loadingProspects.current.delete(prospectId);
        }
      })();

      loadingProspects.current.set(prospectId, requestPromise);
      return await requestPromise;

    } catch (err) {
      loadingProspects.current.delete(prospectId);
      return null;
    }
  }, [prospectId]);

  // Vérifie le statut d'une tâche Google Reviews pour un prospect donné
  const checkStatus = useCallback(async () => {
    try {
      if (!prospectId) {
        setError('ProspectId manquant');
        setLoading(false);
        return;
      }

      const existingData = await checkExistingData();
      if (existingData) {
        setTaskStatus('completed');
        setEstimatedWaitTime(0);
        setBusinessInfo(existingData);
        setError(null);
        setLoading(false);
        return;
      }

      if (isDemoId(prospectId)) {
        setTaskStatus('completed');
        setEstimatedWaitTime(0);
        setBusinessInfo(DEMO_GOOGLE_REVIEWS);
        setError(null);
        setLoading(false);
        return;
      }

      const status = await callEdge('google-reviews-fetch', {
        action: 'checkStatus',
        prospectId
      });

      setTaskStatus(status.status);
      setEstimatedWaitTime(status.estimatedWaitTime || 0);

      if (status.status === 'completed' && status.businessInfo) {
        setBusinessInfo(status.businessInfo);
        setError(null);
        setLoading(false);
      } else if (status.status === 'failed') {
        setError(status.error || 'Erreur inconnue');
        setLoading(false);
      } else if (status.status === 'pending' || status.status === 'processing') {
        setError(null);
      }
    } catch (err) {
      setError('Erreur lors de la vérification du statut');
      setLoading(false);
    }
  }, [prospectId, checkExistingData]);

  // Démarre une tâche Google Reviews avec les paramètres spécifiés
  const startTask = useCallback(async () => {
    try {
      if (!prospectId) {
        setError('ProspectId manquant');
        return;
      }

      const existingData = await checkExistingData();
      if (existingData) {
        setTaskStatus('completed');
        setEstimatedWaitTime(0);
        setBusinessInfo(existingData);
        setError(null);
        setLoading(false);
        return;
      }

      if (isDemoId(prospectId)) {
        setLoading(true);
        setError(null);

        setTimeout(() => {
          setTaskStatus('completed');
          setEstimatedWaitTime(0);
          setBusinessInfo(DEMO_GOOGLE_REVIEWS);
          setError(null);
          setLoading(false);
        }, 1000);
        return;
      }

      setLoading(true);
      setError(null);

      let finalBusinessName = businessName;
      let finalLocation = location;

      if (!finalBusinessName || !finalLocation) {
        try {
          const prospectData = await fetchProspectData();
          finalBusinessName = finalBusinessName || prospectData.company;
          finalLocation = finalLocation || prospectData.location;

          if (!businessName && prospectData.company) {
            setBusinessName(prospectData.company);
          }
          if (!location && prospectData.location) {
            setLocation(prospectData.location);
          }
        } catch (dbError) {
          setError('Impossible de récupérer les données de l\'entreprise');
          setLoading(false);
          return;
        }
      }

      if (!finalBusinessName) {
        setError('Nom d\'entreprise manquant dans les métadonnées');
        setLoading(false);
        return;
      }

      try {
        const response = await callEdge('google-reviews-fetch', {
          action: 'start',
          companyName: finalBusinessName,
          location: finalLocation,
          prospectId,
          domain,
          languageCode,
          reviewsLimit
        });

        setTaskStatus(response.status);

        if (response.status === 'completed' && response.businessInfo) {
          setBusinessInfo(response.businessInfo);
          setEstimatedWaitTime(0);
          setLoading(false);
        } else if (response.estimatedWaitTime !== undefined) {
          const cappedWaitTime = Math.min(response.estimatedWaitTime || 10, 15);
          setEstimatedWaitTime(cappedWaitTime);
          setIsPolling(true);
        }
      } catch (err) {
        setError('Erreur lors du démarrage de la tâche');
        setTaskStatus('failed');
        setLoading(false);
      }
    } catch (err) {
      setError('Erreur lors du démarrage de la tâche');
      setTaskStatus('failed');
      setLoading(false);
    }
  }, [businessName, location, prospectId, domain, languageCode, reviewsLimit, fetchProspectData, checkExistingData]);

  // Relance la tâche Google Reviews
  const retry = useCallback(async () => {
    setError(null);
    setTaskStatus('pending');
    setLoading(true);
    setIsPolling(false);
    setBusinessInfo(null);
    setEstimatedWaitTime(0);

    try {
      await startTask();
    } catch (err) {
      setError('Erreur lors de la relance de la tâche');
      setTaskStatus('failed');
      setLoading(false);
    }
  }, [businessName, location, prospectId, domain, startTask]);

  // Force le rafraîchissement des données depuis la base de données
  const forceRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Récupérer directement les données depuis la base de données
      const existingData = await checkExistingData();

      if (existingData) {
        setTaskStatus('completed');
        setEstimatedWaitTime(0);
        setBusinessInfo(existingData);
        setIsPolling(false);
      } else {
        setTaskStatus('not_found');
        setBusinessInfo(null);
      }
    } catch (err) {
      setError('Erreur lors du rafraîchissement des données');
      setTaskStatus('failed');
    } finally {
      setLoading(false);
    }
  }, [checkExistingData]);

  // Réinitialiser le hook quand le prospect change
  useEffect(() => {
    if (!prospectId) return;
    // Réinitialiser l'état interne pour éviter de réutiliser des données de démo
    setBusinessInfo(null);
    setTaskStatus('not_found');
    setEstimatedWaitTime(0);
    setIsPolling(false);
    setError(null);
    setInitialized(false);
  }, [prospectId]);

  // Initialisation/premier chargement ou après reset
  useEffect(() => {
    if (!prospectId || initialized) return;
    
    setInitialized(true);
    
    const init = async () => {
      setLoading(true);
      try {
        // Récupérer les données de la DB en premier si nécessaire
        if (!businessName || !location) {
          try {
            const prospectData = await fetchProspectData();
            if (!businessName && prospectData.company) {
              setBusinessName(prospectData.company);
            }
            if (!location && prospectData.companyLocation) {
              setLocation(prospectData.companyLocation);
            }
          } catch (dbError) {
            console.warn('Impossible de récupérer les données prospect:', dbError);
          }
        }
        
        // Always check for existing data first
        const existingData = await checkExistingData();

        if (existingData) {
          console.log('✅ [Google Reviews] Données existantes trouvées, mise à jour du statut:', { prospectId, hasData: !!existingData });
          setBusinessInfo(existingData);
          setTaskStatus('completed');
          setLoading(false);
          return;
        }

        console.log('ℹ️ [Google Reviews] Aucune donnée existante, autoStart:', autoStart);

        // Only check status if autoStart is enabled
        if (autoStart) {
          await checkStatus();

          // Si autoStart est activé et qu'aucune tâche n'existe, démarrer automatiquement
          if (taskStatus === 'not_found') {
            await startTask();
          }
        } else {
          setLoading(false);
        }
      } catch (err) {
        setError('Erreur lors de l\'initialisation');
        setLoading(false);
      }
    };

    init();
  }, [prospectId, initialized, autoStart, checkStatus, startTask, taskStatus, fetchProspectData, businessName, location, checkExistingData]);

  // Polling séparé basé sur le taskStatus
  useEffect(() => {
    if (taskStatus !== 'pending' && taskStatus !== 'processing') {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    const interval = setInterval(async () => {
      try {
        await checkStatus();
      } catch (err) {
      }
    }, 2000);

    // Timeout de sécurité réduit à 3 minutes
    const timeout = setTimeout(() => {
      setError('Délai d\'attente dépassé');
      setTaskStatus('failed');
      setLoading(false);
    }, 3 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [taskStatus, checkStatus]);

  return {
    businessInfo,
    loading,
    error,
    taskStatus,
    estimatedWaitTime,
    isPolling,
    startTask,
    checkStatus,
    retry,
    forceRefresh
  };
}
